const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://minexxapi-livescreen-p7n5ing2cq-uc.a.run.app';

/**
 * Fetch export metrics (total, this month, YTD)
 * @param {string} country - Country code (e.g., 'DRC', 'RWANDA')
 * @param {string|string[]} mineral - Mineral type(s)
 * @returns {Promise<Object>} Export metrics data
 */
export const fetchExportMetrics = async (country, mineral) => {
  try {
    const minerals = Array.isArray(mineral) ? mineral : [mineral];
    const mineralParams = minerals.map(m => `mineral=${encodeURIComponent(m)}`).join('&');
    const url = `${API_BASE_URL}/livescreen/export/metrics?country=${encodeURIComponent(country)}&${mineralParams}`;
    
    const response = await fetch(url);
    const data = await response.json();
    
    if (data.success) {
      return data.data;
    } else {
      throw new Error(data.message || 'Failed to fetch export metrics');
    }
  } catch (error) {
    console.error('Error fetching export metrics:', error);
    throw error;
  }
};

/**
 * Fetch purchase metrics (this week, this month, YTD)
 * @param {string} country - Country code
 * @param {string|string[]} mineral - Mineral type(s)
 * @returns {Promise<Object>} Purchase metrics data
 */
export const fetchPurchaseMetrics = async (country, mineral) => {
  try {
    const minerals = Array.isArray(mineral) ? mineral : [mineral];
    const mineralParams = minerals.map(m => `mineral=${encodeURIComponent(m)}`).join('&');
    const url = `${API_BASE_URL}/livescreen/purchases?country=${encodeURIComponent(country)}&${mineralParams}`;
    
    const response = await fetch(url);
    const data = await response.json();
    
    if (data.success) {
      return data.data;
    } else {
      throw new Error(data.message || 'Failed to fetch purchase metrics');
    }
  } catch (error) {
    console.error('Error fetching purchase metrics:', error);
    throw error;
  }
};

/**
 * Fetch container summary (for Rwanda - by location)
 * @param {string} country - Country code
 * @param {string|string[]} mineral - Mineral type(s)
 * @returns {Promise<Object>} Container summary data
 */
export const fetchContainerSummary = async (country, mineral) => {
  try {
    const minerals = Array.isArray(mineral) ? mineral : [mineral];
    const mineralParams = minerals.map(m => `mineral=${encodeURIComponent(m)}`).join('&');
    const url = `${API_BASE_URL}/livescreen/containers?country=${encodeURIComponent(country)}&${mineralParams}`;
    
    const response = await fetch(url);
    const data = await response.json();
    
    if (data.success) {
      return data.data;
    } else {
      throw new Error(data.message || 'Failed to fetch container summary');
    }
  } catch (error) {
    console.error('Error fetching container summary:', error);
    throw error;
  }
};

/**
 * Fetch container summary for DRC (by comptoir)
 * @param {string} country - Country code (should be 'DRC')
 * @param {string|string[]} mineral - Mineral type(s)
 * @returns {Promise<Object>} Container summary DRC data
 */
export const fetchContainerSummaryDrc = async (country, mineral) => {
  try {
    const minerals = Array.isArray(mineral) ? mineral : [mineral];
    const mineralParams = minerals.map(m => `mineral=${encodeURIComponent(m)}`).join('&');
    const url = `${API_BASE_URL}/livescreen/containers/drc?country=${encodeURIComponent(country)}&${mineralParams}`;
    
    const response = await fetch(url);
    const data = await response.json();
    
    if (data.success) {
      return data.data;
    } else {
      throw new Error(data.message || 'Failed to fetch container summary DRC');
    }
  } catch (error) {
    console.error('Error fetching container summary DRC:', error);
    throw error;
  }
};

/**
 * Fetch shipping status (in transit, delivered, delayed)
 * @param {string} country - Country code
 * @param {string|string[]} mineral - Mineral type(s)
 * @returns {Promise<Object>} Shipping status data
 */
export const fetchShippingStatus = async (country, mineral) => {
  try {
    const minerals = Array.isArray(mineral) ? mineral : [mineral];
    const mineralParams = minerals.map(m => `mineral=${encodeURIComponent(m)}`).join('&');
    const url = `${API_BASE_URL}/livescreen/weight/shipping-status?country=${encodeURIComponent(country)}&${mineralParams}`;
    
    const response = await fetch(url);
    const data = await response.json();
    
    if (data.success) {
      return data.data;
    } else {
      throw new Error(data.message || 'Failed to fetch shipping status');
    }
  } catch (error) {
    console.error('Error fetching shipping status:', error);
    throw error;
  }
};

/**
 * Update container status
 * @param {string} country - Country code
 * @param {string} exportId - Export ID
 * @param {string} status - Status ('in transit', 'delivered', 'delay')
 * @returns {Promise<Object>} Update result
 */
export const updateContainerStatus = async (country, exportId, status) => {
  try {
    const url = `${API_BASE_URL}/livescreen/container-status?country=${encodeURIComponent(country)}&exportId=${encodeURIComponent(exportId)}&status=${encodeURIComponent(status)}`;
    
    const response = await fetch(url, {
      method: 'POST'
    });
    const data = await response.json();
    
    if (data.success) {
      return data.data;
    } else {
      throw new Error(data.message || 'Failed to update container status');
    }
  } catch (error) {
    console.error('Error updating container status:', error);
    throw error;
  }
};

/**
 * Fetch latest export by country and platform
 * @param {string} country - Country code
 * @param {string} platform - Platform type (default: '3ts')
 * @returns {Promise<Object>} Latest export data
 */
export const fetchLatestExport = async (country) => {
  try {
    const url = `${API_BASE_URL}/livescreen/export/latest?country=${encodeURIComponent(country)}`;
    
    const response = await fetch(url);
    const data = await response.json();
    
    if (data.success) {
      return data.data;
    } else {
      throw new Error(data.message || 'Failed to fetch latest export');
    }
  } catch (error) {
    console.error('Error fetching latest export:', error);
    throw error;
  }
};
